import { driver } from "driver.js";
import type { Driver } from "driver.js";
import "driver.js/dist/driver.css";
import {
  clearBuscaRedeTourMock,
  isBuscaRedeTourMockMode,
  seedBuscaRedeTourMock,
  setBuscaRedeTourMockMode
} from "@/utils/buscaRedeTourMock";

export const BUSCA_REDE_TOUR_TOTAL_STEPS = 8;
/** Template do opt-in (mesmo do onboard antigo de Busca de Rede) */
export const BUSCA_REDE_TOUR_TEMPLATE = process.env.VUE_APP_AFFILIATE_SEARCH_ONBOARD;
export const BUSCA_REDE_TOUR_ONBOARD_ID = "busca-rede-tour";

/**
 * APIs usadas:
 * - Exibir?  GET  {VUE_APP_SERVER_URL}/MinhaConta/ObtemOptinPendente
 *            query: vinculo, template (VUE_APP_AFFILIATE_SEARCH_ONBOARD), sistema="PWA OMINT ONBOARD"
 *            → mostra se response.Texto existir
 * - Concluir: POST {VUE_APP_SERVER_URL}/Associado/AceitaOptin
 *            query: vinculo, template, login
 */

const SELECTORS = {
  homeShortcut: '.shortcut-item[data-tour="busca-rede-atalho"]',
  homeSearch: '[data-tour="busca-rede-home-search"]',
  categorias: '[data-tour="busca-rede-categorias"]',
  favoritos: '[data-tour="busca-rede-favoritos"]',
  subespecialidade: '[data-tour="busca-rede-subespecialidade"]',
  filtrar: '[data-tour="busca-rede-filtrar"]',
  ordenar: '[data-tour="busca-rede-ordenar"]',
  nomeSearch: '[data-tour="busca-rede-nome-search"]',
  location: '[data-tour="busca-rede-location"]',
  doctorDetailCard: '[data-tour="busca-rede-detalhe-prestador"]'
};

type TourVm = {
  $router: {
    push: (route: string | Record<string, unknown>) => Promise<unknown> | unknown;
    replace: (route: string | Record<string, unknown>) => Promise<unknown> | unknown;
  };
  $root: { $emit: (event: string, ...args: unknown[]) => void };
  $nextTick: (cb?: () => void) => Promise<void> | void;
  $store: {
    state: { affiliate: Record<string, unknown> };
    commit: (type: string, payload?: unknown) => void;
    dispatch: (type: string, payload?: unknown) => Promise<unknown>;
  };
  showSubspecialtyFilterModal?: boolean;
  shouldOpenOnboard?: boolean;
};

let activeDriver: Driver | null = null;
let isNavigatingBetweenPages = false;
/** Progresso só em memória (SPA). */
let tourProgress: number | null = null;
/** Cache da consulta de opt-in na sessão (evita N requests entre páginas do tour). */
let optinAllowedCache: boolean | null = null;

function getProgress(): number | null {
  return tourProgress;
}

function setProgress(step: number) {
  tourProgress = step;
}

function clearProgress() {
  tourProgress = null;
}

/**
 * Decide se o tutorial deve aparecer via API:
 * GET MinhaConta/ObtemOptinPendente → sim se `Texto` vier preenchido
 */
export async function shouldShowBuscaRedeTour(store: TourVm["$store"]): Promise<boolean> {
  if (optinAllowedCache !== null) return optinAllowedCache;

  if (!BUSCA_REDE_TOUR_TEMPLATE || BUSCA_REDE_TOUR_TEMPLATE === "0") {
    optinAllowedCache = false;
    return false;
  }

  try {
    const pendingOptin = (await store.dispatch("saude/getOnboardOptin", {
      template: BUSCA_REDE_TOUR_TEMPLATE
    })) as { Texto?: string } | undefined;

    optinAllowedCache = !!pendingOptin?.Texto;
    return optinAllowedCache;
  } catch (error) {
    console.error("Erro ao validar opt-in do tour Busca de Rede", error);
    optinAllowedCache = false;
    return false;
  }
}

export async function markBuscaRedeTourCompleted(store?: TourVm["$store"]) {
  optinAllowedCache = false;
  clearProgress();
  clearBuscaRedeTourMock(store);

  if (!store || !BUSCA_REDE_TOUR_TEMPLATE || BUSCA_REDE_TOUR_TEMPLATE === "0") return;

  try {
    await store.dispatch("saude/saveClosedOnboard", {
      onboard: BUSCA_REDE_TOUR_ONBOARD_ID,
      sendToServer: true,
      template: BUSCA_REDE_TOUR_TEMPLATE
    });
  } catch (error) {
    console.error("Erro ao gravar AceitaOptin do tour Busca de Rede", error);
  }
}

/** Útil em QA/dev para forçar nova consulta de opt-in */
export function resetBuscaRedeTour() {
  optinAllowedCache = null;
  clearProgress();
  clearBuscaRedeTourMock();
  if (activeDriver) {
    const current = activeDriver;
    activeDriver = null;
    current.destroy();
  }
}

export function shouldAutoSelectSpecialtyForTour(): boolean {
  return false;
}

export function isBuscaRedeTourActive(): boolean {
  return getProgress() !== null;
}

async function destroyDriver(persist: boolean, store?: TourVm["$store"]) {
  if (persist) {
    await markBuscaRedeTourCompleted(store);
  } else {
    clearProgress();
    clearBuscaRedeTourMock(store);
  }

  if (activeDriver) {
    const current = activeDriver;
    activeDriver = null;
    current.destroy();
  }
}

async function finishTourAndGoHome(vm: TourVm) {
  await destroyDriver(true, vm.$store);
  const baseUrl = (process.env.VUE_APP_BASE_URL || "/").replace(/\/$/, "");
  // Full reload limpa estado mock do Vuex e reabre a home da rede
  globalThis.window.location.assign(`${baseUrl}/redecredenciada-home`);
}

function waitForElement(selector: string, timeoutMs = 8000): Promise<Element | null> {
  return new Promise(resolve => {
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }

    const startedAt = Date.now();
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      } else if (Date.now() - startedAt > timeoutMs) {
        observer.disconnect();
        resolve(null);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    window.setTimeout(() => {
      observer.disconnect();
      resolve(document.querySelector(selector));
    }, timeoutMs);
  });
}

function createBaseConfig(store?: TourVm["$store"]) {
  return {
    animate: false,
    overlayColor: "#4E4E4E",
    overlayOpacity: 0.55,
    smoothScroll: false,
    allowClose: true,
    stagePadding: 8,
    stageRadius: 8,
    allowKeyboardControl: true,
    disableActiveInteraction: true,
    popoverClass: "busca-rede-tour-theme",
    showButtons: ["next", "close"] as Array<"next" | "close" | "previous">,
    showProgress: true,
    nextBtnText: "Próximo",
    doneBtnText: "Próximo",
    progressText: "{{current}} de {{total}}",
    onCloseClick: () => {
      destroyDriver(false, store);
    },
    onDestroyStarted: () => {
      if (isNavigatingBetweenPages) {
        isNavigatingBetweenPages = false;
        if (activeDriver) {
          const current = activeDriver;
          activeDriver = null;
          current.destroy();
        }
        return;
      }
      destroyDriver(false, store);
    }
  };
}

async function navigateToDoctorsForTour(vm: TourVm) {
  setProgress(4);
  seedBuscaRedeTourMock(vm.$store);
  isNavigatingBetweenPages = true;
  await vm.$router.push({ name: "affiliate.docs" });
}

export async function maybeStartBuscaRedeTourFromHome(vm: TourVm) {
  if (vm.shouldOpenOnboard) return;
  if (!(await shouldShowBuscaRedeTour(vm.$store))) return;

  const progress = getProgress();
  if (progress !== null && progress > 0) return;

  const shortcut = await waitForElement(SELECTORS.homeShortcut, 4000);
  if (!shortcut) {
    return;
  }

  setProgress(0);
  startHomeTour(vm);
}

export type ArrowAlign = "left" | "center" | "right" | "auto";

export type TourStepPopover = {
  title: string;
  description: string;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
  arrowAlign?: ArrowAlign;
  arrowTarget?: string;
  popoverClass?: string;
  progressText?: string;
  nextBtnText?: string;
  prevBtnText?: string;
  doneBtnText?: string;
  onNextClick?: () => void | Promise<void>;
  onPrevClick?: () => void | Promise<void>;
};

export type TourStep = {
  element?: string;
  arrowTarget?: string;
  popover: TourStepPopover;
  onHighlighted?: () => void;
  onDeselected?: () => void;
};

// Localiza o elemento de referência da seta se arrowTarget for informado
function resolvePointerElement(step: TourStep, focusElement: Element | null): Element | null {
  const arrowTarget = step.arrowTarget || step.popover.arrowTarget;
  if (!arrowTarget) return focusElement;

  if (arrowTarget.startsWith("[") || arrowTarget.startsWith(".") || arrowTarget.startsWith("#")) {
    const el = document.querySelector(arrowTarget);
    if (el) return el;
  }

  const el =
    document.querySelector(`[data-tour-arrow="${arrowTarget}"]`) ||
    document.querySelector(`[data-tooltip-arrow="${arrowTarget}"]`) ||
    document.querySelector(arrowTarget);

  return el || focusElement;
}

// Calcula a posição da seta baseada no elemento alvo
export function calculateAutoArrowAlign(el: Element): "left" | "center" | "right" {
  const rect = el.getBoundingClientRect();
  const elementCenter = rect.left + rect.width / 2;
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 0;
  if (!viewportWidth) return "center";

  if (elementCenter < viewportWidth / 3) {
    return "left";
  }
  if (elementCenter > (viewportWidth / 3) * 2) {
    return "right";
  }
  return "center";
}

// Processa os passos do tour
function processSteps(steps: TourStep[], vm?: TourVm) {
  return steps.map(step => {
    const {
      arrowAlign,
      popoverClass,
      onNextClick: originalOnNextClick,
      onPrevClick: originalOnPrevClick,
      ...restPopover
    } = step.popover;

    let computedClass = popoverClass || "busca-rede-tour-theme";
    if (arrowAlign === "left") {
      computedClass = `${computedClass} driver-popover-arrow-align-left`;
    } else if (arrowAlign === "right") {
      computedClass = `${computedClass} driver-popover-arrow-align-right`;
    }

    const onNextClick = async () => {
      const popoverEl = document.querySelector(".driver-popover") as HTMLElement | null;
      if (popoverEl) {
        popoverEl.classList.add("driver-popover-calculating");
      }
      if (originalOnNextClick) {
        await originalOnNextClick();
      }
    };

    const onPrevClick = originalOnPrevClick
      ? async () => {
          const popoverEl = document.querySelector(".driver-popover") as HTMLElement | null;
          if (popoverEl) {
            popoverEl.classList.add("driver-popover-calculating");
          }
          await originalOnPrevClick();
        }
      : undefined;

    const originalOnHighlighted = step.onHighlighted;
    const onHighlighted = () => {
      const popoverEl = document.querySelector(".driver-popover") as HTMLElement | null;
      if (popoverEl) {
        popoverEl.classList.add("driver-popover-calculating");
      }

      const applyStepPosition = () => {
        if (step.element) {
          const focusEl = typeof step.element === "string" ? document.querySelector(step.element) : step.element;
          if (focusEl) {
            const rect = focusEl.getBoundingClientRect();
            const isInViewport =
              rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
            if (!isInViewport) {
              focusEl.scrollIntoView({ block: "start", inline: "nearest", behavior: "auto" });
            }

            activeDriver?.refresh();

            const pointerEl = resolvePointerElement(step, focusEl) || focusEl;
            const align = arrowAlign === "auto" ? calculateAutoArrowAlign(pointerEl) : arrowAlign;

            const popEl = document.querySelector(".driver-popover") as HTMLElement | null;
            if (popEl) {
              popEl.classList.remove("driver-popover-arrow-align-left", "driver-popover-arrow-align-right");
              if (align === "left") {
                popEl.classList.add("driver-popover-arrow-align-left");
              } else if (align === "right") {
                popEl.classList.add("driver-popover-arrow-align-right");
              }

              // Se tiver arrowTarget específico, calibra o alinhamento da seta diretamente com o pointerElement
              const isSpecificTarget = !!(step.arrowTarget || step.popover.arrowTarget);
              if (isSpecificTarget && pointerEl) {
                const pointerRect = pointerEl.getBoundingClientRect();
                const pointerCenter = pointerRect.left + pointerRect.width / 2;
                const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
                const isDesktop = viewportWidth >= 768;

                const placePopoverOverTarget = () => {
                  const popoverRect = popEl.getBoundingClientRect();
                  const targetLeft = Math.max(
                    16,
                    Math.min(viewportWidth - popoverRect.width - 16, pointerCenter - popoverRect.width / 2)
                  );
                  popEl.style.left = `${targetLeft}px`;
                  return popEl.getBoundingClientRect();
                };

                if (isDesktop) {
                  placePopoverOverTarget();
                } else {
                  const popoverRect = placePopoverOverTarget();
                  const arrowEl = popEl.querySelector(".driver-popover-arrow") as HTMLElement | null;
                  if (arrowEl) {
                    const relativeArrowLeft = Math.max(
                      16,
                      Math.min(popoverRect.width - 26, pointerCenter - popoverRect.left - 10)
                    );
                    arrowEl.style.left = `${relativeArrowLeft}px`;
                    arrowEl.style.right = "auto";
                    arrowEl.style.marginLeft = "0";
                    arrowEl.style.marginRight = "0";
                  }
                }
              }
            }
          }
        } else {
          activeDriver?.refresh();
        }

        originalOnHighlighted?.();

        requestAnimationFrame(() => {
          const popEl = document.querySelector(".driver-popover") as HTMLElement | null;
          if (popEl) {
            popEl.classList.remove("driver-popover-calculating");
          }
        });
      };

      if (vm?.$nextTick) {
        vm.$nextTick(() => {
          applyStepPosition();
        });
      } else {
        applyStepPosition();
      }
    };

    return {
      ...step,
      onHighlighted,
      popover: {
        ...restPopover,
        arrowAlign,
        popoverClass: computedClass,
        onNextClick,
        onPrevClick
      }
    };
  });
}

function startHomeTour(vm: TourVm) {
  if (activeDriver) {
    const current = activeDriver;
    activeDriver = null;
    current.destroy();
  }

  activeDriver = driver({
    ...createBaseConfig(vm.$store),
    steps: processSteps(
      [
        {
          element: SELECTORS.homeShortcut,
          arrowTarget: SELECTORS.homeShortcut,
          popover: {
            title: "Uma nova busca de rede para você",
            description:
              "A nova experiência de busca está mais rápida, intuitiva e conta com recursos de inteligência artificial para ajudar você a encontrar o que precisa.",
            side: "top" as const,
            align: "center" as const,
            arrowAlign: "center" as const,
            arrowTarget: SELECTORS.homeShortcut,
            progressText: "1 de 8",
            nextBtnText: "Próximo",
            doneBtnText: "Próximo",
            onNextClick: async () => {
              setProgress(1);
              isNavigatingBetweenPages = true;
              if (activeDriver) {
                const current = activeDriver;
                activeDriver = null;
                current.destroy();
              }
              await vm.$router.push("/redecredenciada-home");
            }
          }
        }
      ],
      vm
    )
  });

  activeDriver.drive(0);
}

export async function maybeResumeBuscaRedeTourOnAffiliateHome(vm: TourVm) {
  if (!(await shouldShowBuscaRedeTour(vm.$store))) return;

  let progress = getProgress();

  if (progress === null || progress === 0) {
    setProgress(1);
    progress = 1;
  }

  if (progress >= 4) {
    setProgress(1);
    progress = 1;
  }

  if (progress !== 1 && progress !== 2 && progress !== 3) return;

  const searchEl = await waitForElement(SELECTORS.homeSearch, 8000);
  if (!searchEl) return;

  const startIndex = progress === 1 ? 0 : progress === 2 ? 1 : 2;
  startAffiliateHomeTour(vm, startIndex);
}

function startAffiliateHomeTour(vm: TourVm, startIndex = 0) {
  if (activeDriver) {
    const current = activeDriver;
    activeDriver = null;
    current.destroy();
  }

  activeDriver = driver({
    ...createBaseConfig(vm.$store),
    steps: processSteps(
      [
        {
          element: SELECTORS.homeSearch,
          popover: {
            title: "Encontre os serviços que você precisa usando nosso Assistente de IA",
            description: "",
            side: "bottom" as const,
            align: "center" as const,
            arrowAlign: "left" as const,
            progressText: "2 de 8",
            nextBtnText: "Próximo",
            doneBtnText: "Próximo",
            onNextClick: async () => {
              const categorias = document.querySelector(SELECTORS.categorias);
              const favoritos = document.querySelector(SELECTORS.favoritos);
              if (!categorias && !favoritos) {
                if (activeDriver) {
                  const current = activeDriver;
                  activeDriver = null;
                  current.destroy();
                }
                await navigateToDoctorsForTour(vm);
                return;
              }

              setProgress(2);
              activeDriver?.moveNext();
            }
          }
        },
        {
          element: SELECTORS.categorias,
          popover: {
            title: "Se preferir, faça a busca guiada de forma fácil e intuitiva.",
            description: "",
            side: "bottom" as const,
            align: "center" as const,
            arrowAlign: "left" as const,
            progressText: "3 de 8",
            nextBtnText: "Próximo",
            doneBtnText: "Próximo",
            onNextClick: async () => {
              const favoritos = document.querySelector(SELECTORS.favoritos);
              if (!favoritos) {
                if (activeDriver) {
                  const current = activeDriver;
                  activeDriver = null;
                  current.destroy();
                }
                await navigateToDoctorsForTour(vm);
                return;
              }

              setProgress(3);
              activeDriver?.moveNext();
            }
          }
        },
        {
          element: SELECTORS.favoritos,
          popover: {
            title: "Acesse seus favoritos",
            description: "Seus prestadores favoritos em um só lugar.",
            side: "bottom" as const,
            align: "start" as const,
            arrowAlign: "auto" as const,
            progressText: "4 de 8",
            nextBtnText: "Próximo",
            doneBtnText: "Próximo",
            onNextClick: async () => {
              if (activeDriver) {
                const current = activeDriver;
                activeDriver = null;
                current.destroy();
              }
              await navigateToDoctorsForTour(vm);
            }
          }
        }
      ],
      vm
    )
  });

  activeDriver.drive(startIndex);
}

export async function maybeResumeBuscaRedeTourOnDoctorsList(vm: TourVm) {
  if (!(await shouldShowBuscaRedeTour(vm.$store))) return;

  let progress = getProgress();
  if (progress === null || progress < 4) {
    setProgress(4);
    progress = 4;
  }

  const filtrarEl = await waitForElement(SELECTORS.filtrar, 8000);
  if (!filtrarEl) return;

  const startIndex = progress === 4 ? 0 : progress === 5 ? 1 : 2;

  startDoctorsListTour(vm, startIndex);
}

async function navigateToDoctorInfoForTour(vm: TourVm) {
  setProgress(7);
  setBuscaRedeTourMockMode(true);
  isNavigatingBetweenPages = true;
  if (activeDriver) {
    const current = activeDriver;
    activeDriver = null;
    current.destroy();
  }
  await vm.$router.push({
    name: "affiliate.docInfo",
    params: {
      situacao: "A",
      rede: "TOUR",
      cdPrestador: "TOUR-001",
      srEndereco: "1"
    }
  });
}

function startDoctorsListTour(vm: TourVm, startIndex = 0) {
  if (activeDriver) {
    const current = activeDriver;
    activeDriver = null;
    current.destroy();
  }

  const allSteps: TourStep[] = [
    {
      element: SELECTORS.filtrar,
      popover: {
        title: "Filtre os resultados",
        description:
          "Use os filtros inteligentes para refinar sua busca e localizar prestadores por idioma, telemedicina, agendamento online, WhatsApp e outras opções.",
        side: "bottom" as const,
        align: "center" as const,
        arrowAlign: "left" as const,
        progressText: "5 de 8",
        nextBtnText: "Próximo",
        doneBtnText: "Próximo",
        onNextClick: () => {
          setProgress(5);
          activeDriver?.moveNext();
        }
      }
    },
    {
      element: SELECTORS.ordenar,
      popover: {
        title: "Ordene os resultados",
        description: "Ordene os resultados por: Indicação Omint, Distância ou Rede e distância.",
        side: "bottom" as const,
        align: "center" as const,
        progressText: "6 de 8",
        nextBtnText: "Próximo",
        doneBtnText: "Próximo",
        onNextClick: () => {
          setProgress(6);
          activeDriver?.moveNext();
        }
      }
    },
    {
      element: SELECTORS.location,
      popover: {
        title: "Defina o ponto de partida da sua busca",
        description:
          "Informe a localização para receber resultados mais relevantes e encontrar os prestadores mais próximos.",
        side: "bottom" as const,
        align: "center" as const,
        arrowAlign: "left" as const,
        progressText: "7 de 8",
        nextBtnText: "Próximo",
        doneBtnText: "Próximo",
        onNextClick: async () => {
          await navigateToDoctorInfoForTour(vm);
        }
      }
    }
  ];

  const filteredSteps = allSteps.filter(step => {
    if (step.element === SELECTORS.location) {
      return !!document.querySelector(step.element);
    }
    return true;
  });

  // Se o elemento de localização não existir, o último step navega para os detalhes
  if (filteredSteps.length > 0) {
    const lastStep = filteredSteps[filteredSteps.length - 1];
    if (lastStep.popover) {
      lastStep.popover.onNextClick = async () => {
        await navigateToDoctorInfoForTour(vm);
      };
    }
  }

  const resolvedSteps = processSteps(filteredSteps, vm);

  if (!resolvedSteps.length) {
    clearProgress();
    clearBuscaRedeTourMock(vm.$store);
    return;
  }

  activeDriver = driver({
    ...createBaseConfig(vm.$store),
    steps: resolvedSteps
  });

  const safeIndex = Math.min(startIndex, resolvedSteps.length - 1);
  activeDriver.drive(safeIndex);
}

export async function maybeResumeBuscaRedeTourOnDoctorInfo(vm: TourVm) {
  if (!(await shouldShowBuscaRedeTour(vm.$store))) return;

  let progress = getProgress();
  if (progress === null || progress < 7) {
    setProgress(7);
    progress = 7;
  }

  startDoctorInfoTour(vm);
}

function startDoctorInfoTour(vm: TourVm) {
  if (activeDriver) {
    const current = activeDriver;
    activeDriver = null;
    current.destroy();
  }

  const step: TourStep = {
    popover: {
      title: "Todas as informações do prestador em um só lugar",
      description:
        "Encontre tudo o que você precisa para escolher o prestador ideal: endereço, telefones, WhatsApp, agendamento online, currículo, facilidades e idiomas. Você também pode favoritar e compartilhar o perfil com facilidade.",
      progressText: "8 de 8",
      doneBtnText: "Concluir",
      nextBtnText: "Concluir",
      onNextClick: () => {
        finishTourAndGoHome(vm);
      }
    }
  };

  const resolvedSteps = processSteps([step], vm);
  if (!resolvedSteps.length) {
    clearProgress();
    clearBuscaRedeTourMock(vm.$store);
    return;
  }

  activeDriver = driver({
    ...createBaseConfig(vm.$store),
    steps: resolvedSteps
  });

  activeDriver.drive(0);
}

export function stopBuscaRedeTour(persist = false, store?: TourVm["$store"]) {
  isNavigatingBetweenPages = false;
  destroyDriver(persist, store);
}

export function forceEnableBuscaRedeTour() {
  optinAllowedCache = true;
  clearProgress();
}

export { isBuscaRedeTourMockMode };

if (typeof window !== "undefined") {
  const win = window as Window & { resetBuscaRedeTour?: () => void; forceEnableBuscaRedeTour?: () => void };
  win.resetBuscaRedeTour = resetBuscaRedeTour;
  win.forceEnableBuscaRedeTour = forceEnableBuscaRedeTour;
}
